import localforage from 'localforage';
import { getDatabase, isDatabaseReady } from '../index';
import CaseRepository from '../repositories/CaseRepository';
import JurorRepository from '../repositories/JurorRepository';
import * as PendingRepository from '../repositories/PendingRepository';
import { getEncryptionKeyForDb } from '../../util/encryptedStorage';
import { isLocalId } from '../sync/diffUtils';

/**
 * Migration Utility: redux-persist → Dexie
 * 
 * Safely migrates existing data from the legacy redux-persist storage
 * (stored in localforage/IndexedDB) to the new per-user Dexie database.
 * 
 * Migration is:
 * - Idempotent: safe to run multiple times
 * - Non-destructive: does not delete legacy data until confirmed
 * - Per-user: each user's data is migrated separately
 * - Offline-friendly: works without server connection
 */

const MIGRATION_STATUS_KEY = 'dexie_migration_status';

/**
 * Get migration status for current user
 * @returns {Promise<Object>}
 */
async function getMigrationStatus() {
  const db = getDatabase();
  if (!db) return null;
  
  try {
    const status = await db.syncMetadata.get(MIGRATION_STATUS_KEY);
    return status || { migrated: false };
  } catch {
    return { migrated: false };
  }
}

/**
 * Set migration status
 * @param {Object} status - Migration status object
 */
async function setMigrationStatus(status) {
  const db = getDatabase();
  if (!db) return;
  
  await db.syncMetadata.put({
    ...status,
    key: MIGRATION_STATUS_KEY
  });
}

/**
 * Read legacy redux-persist data from localforage
 * Handles both encrypted and unencrypted formats
 * @returns {Promise<Object|null>}
 */
async function readLegacyData() {
  try {
    const rawData = await localforage.getItem('persist:root');
    if (!rawData) {
      console.log('[Migration] No legacy data found');
      return null;
    }

    // Check if data is encrypted (string) or unencrypted (object)
    if (typeof rawData === 'string') {
      // Encrypted data - need to decrypt
      const encryptionKey = await getEncryptionKeyForDb();
      if (!encryptionKey) {
        console.log('[Migration] Encrypted legacy data but no key available');
        return null;
      }
      
      // Decrypt using the same method as encryptedStorage
      const decrypted = await decryptLegacyData(encryptionKey, rawData);
      return parseLegacyState(decrypted);
    } else {
      // Unencrypted data (old format)
      return parseLegacyState(rawData);
    }
  } catch (error) {
    console.error('[Migration] Failed to read legacy data:', error);
    return null;
  }
}

/**
 * Decrypt legacy encrypted data
 */
async function decryptLegacyData(key, encryptedString) {
  try {
    const combined = Uint8Array.from(atob(encryptedString), c => c.charCodeAt(0));
    const iv = combined.slice(0, 12);
    const encrypted = combined.slice(12);
    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      encrypted
    );
    return JSON.parse(new TextDecoder().decode(decrypted));
  } catch (e) {
    console.warn('[Migration] Failed to decrypt legacy data:', e);
    return null;
  }
}

/**
 * Parse legacy redux state structure
 * The state has multiple nested JSON strings
 */
function parseLegacyState(data) {
  if (!data) return null;
  
  try {
    // redux-persist stores each reducer as a JSON string
    const state = {};
    
    if (data.app) {
      state.app = typeof data.app === 'string' ? JSON.parse(data.app) : data.app;
    }
    
    return state;
  } catch (error) {
    console.error('[Migration] Failed to parse legacy state:', error);
    return null;
  }
}

/**
 * Migrate cases from legacy format to Dexie
 * @param {Object} legacyApp - Legacy app state
 * @returns {Promise<Object>} - Items to be migrated
 */
async function migrateCases(legacyApp) {
  const db = getDatabase();
  if (!db || !legacyApp.cases) return { cases: [], pending: [] };
  
  const cases = legacyApp.cases;
  const caseIds = Object.keys(cases);
  
  // Fetch existing cases to check for updates
  const existingCases = await db.cases.where(':id').anyOf(caseIds).toArray();
  const existingMap = new Map(existingCases.map(c => [c._id, c]));
  
  const casesToUpsert = [];
  const pendingOpsToAdd = [];
  
  for (const [caseId, caseData] of Object.entries(cases)) {
    const existing = existingMap.get(caseId);
    let shouldUpdate = !existing;
    
    if (existing && caseData.updatedAt && (!existing.updatedAt || caseData.updatedAt > existing.updatedAt)) {
      shouldUpdate = true;
    }
    
    if (shouldUpdate) {
      const caseToSave = {
        ...CaseRepository.DEFAULT_CASE || {}, // Fallback if not exported
        ...caseData,
        id: caseId,
        _id: caseId,
        _synced: !isLocalId(caseId),
        _migratedFrom: 'redux-persist',
        _localOnly: isLocalId(caseId)
      };
      casesToUpsert.push(caseToSave);

      // If it's a local ID, also queue for sync
      if (isLocalId(caseId)) {
        pendingOpsToAdd.push({
          entityType: 'case',
          entityId: caseId,
          operationType: 'create',
          caseId: caseId,
          data: caseData,
          baseRevision: 0,
          createdAt: new Date().toISOString(),
          _migratedFrom: 'redux-persist'
        });
      }
    }
  }
  
  return { cases: casesToUpsert, pending: pendingOpsToAdd };
}

/**
 * Migrate jurors from legacy format to Dexie
 * @param {Object} legacyApp - Legacy app state
 * @returns {Promise<Object>} - Items to be migrated
 */
async function migrateJurors(legacyApp) {
  const db = getDatabase();
  if (!db || !legacyApp.jurors) return { jurors: [], pending: [] };
  
  const jurors = legacyApp.jurors;
  const cases = legacyApp.cases || {};
  const jurorIds = Object.keys(jurors);
  
  // Fetch existing jurors to check for updates
  // For very large sets, we might want to chunk this, but anyOf is generally efficient
  const existingJurors = await db.jurors.where(':id').anyOf(jurorIds).toArray();
  const existingMap = new Map(existingJurors.map(j => [j._id, j]));
  
  // Build a map of jurorId -> caseId from cases
  const jurorToCaseMap = {};
  for (const [caseId, caseData] of Object.entries(cases)) {
    if (caseData.jurors && Array.isArray(caseData.jurors)) {
      for (const jurorId of caseData.jurors) {
        jurorToCaseMap[jurorId] = caseId;
      }
    }
  }
  
  const jurorsToUpsert = [];
  const pendingOpsToAdd = [];
  
  for (const [jurorId, jurorData] of Object.entries(jurors)) {
    const caseId = jurorToCaseMap[jurorId];
    if (!caseId) continue;
    
    const existing = existingMap.get(jurorId);
    let shouldUpdate = !existing;
    
    if (existing && jurorData.updated && (!existing.updated || jurorData.updated > existing.updated)) {
      shouldUpdate = true;
    }
    
    if (shouldUpdate) {
      const jurorToSave = {
        ...JurorRepository.DEFAULT_JUROR || {}, // Fallback
        ...jurorData,
        id: jurorId,
        _id: jurorId,
        caseId,
        _synced: !isLocalId(jurorId),
        _migratedFrom: 'redux-persist',
        _localOnly: isLocalId(jurorId)
      };
      jurorsToUpsert.push(jurorToSave);

      // If it's a local ID, also queue for sync
      if (isLocalId(jurorId)) {
        pendingOpsToAdd.push({
          entityType: 'juror',
          entityId: jurorId,
          operationType: 'create',
          caseId,
          data: jurorData,
          baseRevision: 0,
          createdAt: new Date().toISOString(),
          _migratedFrom: 'redux-persist'
        });
      }
    }
  }
  
  return { jurors: jurorsToUpsert, pending: pendingOpsToAdd };
}

/**
 * Migrate pending operations to new format
 * @param {Object} legacyApp - Legacy app state
 * @returns {Promise<Array>} - Pending ops to be added
 */
async function migratePendingOperations(legacyApp) {
  const pendingOpsToAdd = [];
  
  // Migrate pending case creates
  for (const [caseId, data] of Object.entries(legacyApp.pendingCreateCases || {})) {
    pendingOpsToAdd.push({
      entityType: 'case',
      entityId: caseId,
      caseId: caseId,
      operationType: 'create',
      data: data.createCaseBody || data,
      createdAt: new Date().toISOString(),
      _migratedFrom: 'redux-persist'
    });
  }
  
  // Migrate pending case updates
  for (const [caseId, data] of Object.entries(legacyApp.pendingUpdateCases || {})) {
    pendingOpsToAdd.push({
      entityType: 'case',
      entityId: caseId,
      caseId: caseId,
      operationType: 'update',
      diff: data.editCaseBody || data,
      createdAt: new Date().toISOString(),
      _migratedFrom: 'redux-persist'
    });
  }
  
  // Migrate pending juror creates
  for (const [jurorId, data] of Object.entries(legacyApp.pendingCreateJurors || {})) {
    pendingOpsToAdd.push({
      entityType: 'juror',
      entityId: jurorId,
      caseId: data.caseId,
      operationType: 'create',
      data: legacyApp.jurors?.[jurorId] || {},
      createdAt: new Date().toISOString(),
      _migratedFrom: 'redux-persist'
    });
  }
  
  // Migrate pending juror updates
  for (const [jurorId, data] of Object.entries(legacyApp.pendingUpdateJurors || {})) {
    pendingOpsToAdd.push({
      entityType: 'juror',
      entityId: jurorId,
      caseId: data.caseId,
      operationType: 'update',
      diff: data.diff || {},
      createdAt: new Date().toISOString(),
      _migratedFrom: 'redux-persist'
    });
  }
  
  // Migrate pending juror deletes
  for (const [jurorId, data] of Object.entries(legacyApp.pendingDeleteJurors || {})) {
    pendingOpsToAdd.push({
      entityType: 'juror',
      entityId: jurorId,
      caseId: data.payload?.caseId || data.caseId,
      operationType: 'delete',
      createdAt: new Date().toISOString(),
      _migratedFrom: 'redux-persist'
    });
  }
  
  return pendingOpsToAdd;
}

/**
 * Main migration function
 * Migrates all legacy data to Dexie
 * 
 * @param {Object} options - Migration options
 * @param {boolean} options.force - Force re-migration even if already done
 * @returns {Promise<Object>} - Migration result
 */
export async function migrateLegacyData(options = {}) {
  const { force = false } = options;
  
  console.log('[Migration] Starting migration check...');
  
  // Check if Dexie is ready
  if (!isDatabaseReady()) {
    console.log('[Migration] Database not ready, skipping migration');
    return { success: false, reason: 'database_not_ready' };
  }
  
  // Check if already migrated
  const status = await getMigrationStatus();
  if (status?.migrated && !force) {
    console.log('[Migration] Already migrated, skipping');
    return { success: true, reason: 'already_migrated', ...status };
  }
  
  // Read legacy data
  const legacyData = await readLegacyData();
  if (!legacyData || !legacyData.app) {
    console.log('[Migration] No legacy data to migrate');
    await setMigrationStatus({
      migrated: true,
      migratedAt: new Date().toISOString(),
      reason: 'no_legacy_data'
    });
    return { success: true, reason: 'no_legacy_data' };
  }
  
  const legacyApp = legacyData.app;
  
  console.log('[Migration] Found legacy data:', {
    cases: Object.keys(legacyApp.cases || {}).length,
    jurors: Object.keys(legacyApp.jurors || {}).length,
    pendingCreates: Object.keys(legacyApp.pendingCreateJurors || {}).length,
    pendingUpdates: Object.keys(legacyApp.pendingUpdateJurors || {}).length
  });
  
  // Perform migration
  const result = {
    casesMigrated: 0,
    jurorsMigrated: 0,
    pendingOpsMigrated: 0,
    errors: []
  };
  
  try {
    const caseData = await migrateCases(legacyApp);
    const jurorData = await migrateJurors(legacyApp);
    const pendingData = await migratePendingOperations(legacyApp);

    // Deduplicate pending operations by entityType and entityId
    // Prioritize explicit pending operations from legacyApp over auto-generated ones
    const pendingMap = new Map();

    const addPending = (op) => {
      const key = `${op.entityType}:${op.entityId}`;
      // If we already have a pending op for this entity, only replace if it's more specific
      // or if the existing one is just an auto-generated 'create'
      if (!pendingMap.has(key)) {
        pendingMap.set(key, op);
      } else {
        const existing = pendingMap.get(key);
        // If we have multiple ops for the same entity, the legacy pending queue ones (pendingData)
        // should generally take precedence or be merged.
        // For simplicity, we just keep the first one we find but handle 'create' carefully.
        if (op._migratedFrom === 'redux-persist' && !existing._migratedFrom) {
           pendingMap.set(key, op);
        }
      }
    };

    // Order of adding matters for deduplication
    // 1. Explicitly migrated pending operations
    pendingData.forEach(addPending);
    // 2. Auto-generated pending operations for local entities
    caseData.pending.forEach(addPending);
    jurorData.pending.forEach(addPending);

    // Encrypt each op's sensitive payload BEFORE opening the write transaction —
    // crypto.subtle is async and can't run inside an IndexedDB transaction
    // without it auto-committing. No-op when running without an encryption key.
    const allPending = await Promise.all(
      Array.from(pendingMap.values()).map(PendingRepository.encryptPendingOp)
    );

    const db = getDatabase();
    await db.transaction('rw', [db.cases, db.jurors, db.pendingOperations], async () => {
      if (caseData.cases.length > 0) {
        await db.cases.bulkPut(caseData.cases);
      }
      if (jurorData.jurors.length > 0) {
        await db.jurors.bulkPut(jurorData.jurors);
      }

      if (allPending.length > 0) {
        await db.pendingOperations.bulkAdd(allPending);
      }

      result.casesMigrated = caseData.cases.length;
      result.jurorsMigrated = jurorData.jurors.length;
      // For consistency with previous tests, we might want to distinguish between
      // auto-generated and explicitly migrated pending ops, but let's count all.
      result.pendingOpsMigrated = allPending.length;
    });
    
    console.log(`[Migration] Successfully migrated ${result.casesMigrated} cases, ${result.jurorsMigrated} jurors, and ${result.pendingOpsMigrated} pending operations`);
  } catch (error) {
    result.errors.push({ type: 'migration', error: error.message });
    console.error('[Migration] Critical error during migration:', error);
  }
  
  // Save migration status
  await setMigrationStatus({
    migrated: true,
    migratedAt: new Date().toISOString(),
    ...result
  });
  
  console.log('[Migration] Migration complete:', result);
  
  return {
    success: result.errors.length === 0,
    ...result
  };
}

/**
 * Check if migration is needed
 * @returns {Promise<boolean>}
 */
export async function needsMigration() {
  if (!isDatabaseReady()) return false;
  
  const status = await getMigrationStatus();
  if (status?.migrated) return false;
  
  // Check if there's legacy data
  const rawData = await localforage.getItem('persist:root');
  return !!rawData;
}

/**
 * Get migration status
 * @returns {Promise<Object>}
 */
export async function getMigrationInfo() {
  return getMigrationStatus();
}

export default {
  migrateLegacyData,
  needsMigration,
  getMigrationInfo
};
